/**
 * Types for AssetManagement component
 */

// Asset types for text assets
export type TextAssetType =
  | 'headline'
  | 'description'
  | 'long_headline'
  | 'sitelink'
  | 'callout'
  | 'structured_snippet';

// Image asset types
export type ImageAssetType =
  | 'marketing_image'
  | 'square_marketing_image'
  | 'portrait_marketing_image'
  | 'logo'
  | 'landscape_logo';

// Entity types
export type EntityType = 'campaign' | 'asset_group' | 'ad_group' | 'ad';

// Campaign types that support assets
export type SupportedCampaignType =
  | 'PERFORMANCE_MAX'
  | 'SEARCH'
  | 'DISPLAY'
  | 'SHOPPING'
  | 'AI_MAX_SEARCH';

// Asset level configuration for dropdown
export interface AssetLevelConfig {
  id: string;
  name: string;
  type: EntityType;
  supportedTextAssets: TextAssetType[];
  supportedImageTypes: ImageAssetType[];
}

// Individual asset
export interface ExistingAsset {
  text: string;
  asset_type: string;
  asset_id?: string;
  status?: string;
  final_url?: string; // For sitelinks
}

// RSA (Responsive Search Ad) for editing
export interface RSAData {
  ad_id: string;
  ad_name?: string;
  headlines: Array<{ text: string; pinned_field?: string }>;
  descriptions: Array<{ text: string; pinned_field?: string }>;
  final_url?: string;
  path1?: string;
  path2?: string;
}

// RDA (Responsive Display Ad) for editing
export interface RDAData {
  ad_id: string;
  ad_name?: string;
  headlines: string[];
  long_headline: string;
  descriptions: string[];
  business_name: string;
  final_urls: string[];
  marketing_images: Array<{ asset_id: string; url?: string }>;
  square_marketing_images: Array<{ asset_id: string; url?: string }>;
  logo_images: Array<{ asset_id: string; url?: string }>;
  landscape_logo_images: Array<{ asset_id: string; url?: string }>;
}

// Asset recommendation from AI
export interface RecommendedAsset {
  text: string;
  confidence?: number;
  reason?: string;
}

// Campaign-level asset (sitelinks, callouts, etc.)
export interface CampaignLevelAsset {
  text: string;
  asset_id?: string;
  status?: 'ENABLED' | 'PAUSED' | 'REMOVED';
  final_url?: string; // For sitelinks
}

// Tab types
export type AssetTab = 'text' | 'images';

// Status message type
export interface StatusMessage {
  type: 'success' | 'error' | 'info' | 'warning';
  text: string;
}

// RDA image asset type
export interface RDACacheImageAsset {
  asset_id: string;
  url?: string;
  width?: number;
  height?: number;
}

// RDA cache entry type (matches useRDACache) - includes all data needed for RDAEditor
export interface RDACacheEntry {
  headlines: string[];
  long_headline?: string;
  business_name?: string;
  descriptions?: string[];
  final_urls?: string[];
  // Image assets (with thumbnail URLs merged in)
  marketing_images?: RDACacheImageAsset[];
  square_marketing_images?: RDACacheImageAsset[];
  logo_images?: RDACacheImageAsset[];
  landscape_logo_images?: RDACacheImageAsset[];
  // Thumbnail map for RDAEditor to use (google_ads_asset_id -> thumbnail_url)
  thumbnailMap?: Record<string, string>;
}

// Props for main AssetManagement component
export interface AssetManagementProps {
  campaign: {
    campaign_id: string;
    campaign_name?: string;
    campaign_type?: string;
    google_ads_account_id?: string;
    asset_group_list?: AssetGroupData[];
    ads_group_list?: AdGroupData[];
    sitelinks?: CampaignLevelAsset[];
    callouts?: CampaignLevelAsset[];
    structured_snippets?: Array<{
      header?: string;
      values?: string[];
      asset_id?: string;
      status?: string;
    }>;
  };
  selectedLandingPages?: Map<string, boolean>;
  rdaCache?: Record<string, RDACacheEntry>;
  isLoadingRDAs?: boolean;
}

// Asset group data
export interface AssetGroupData {
  asset_group_id: string;
  asset_group_name: string;
  status?: 'ENABLED' | 'PAUSED' | 'REMOVED';
  final_urls?: Array<{ url: string } | string>;
  asset_list?: Array<{
    content?: string;
    text?: string;
    asset_text?: string;
    asset_id?: string;
    status?: string;
    type?: string;
    asset_type?: string;
  }>;
  marketing_images?: ImageAssetData[];
  square_marketing_images?: ImageAssetData[];
  portrait_marketing_images?: ImageAssetData[];
  logo_images?: ImageAssetData[];
}

// Ad group data
export interface AdGroupData {
  ad_group_id?: string;
  ads_group_id?: string; // Legacy field
  ad_group_name?: string;
  ads_group_name?: string; // Legacy field
  status?: 'ENABLED' | 'PAUSED' | 'REMOVED';
  ads_list?: AdData[];
  ads?: AdData[];
}

// Ad data (for RSAs and RDAs)
export interface AdData {
  ad_id: string;
  ad_name?: string;
  ad_type?: string;  // 'RESPONSIVE_SEARCH_AD' | 'RESPONSIVE_DISPLAY_AD' | etc
  status?: 'ENABLED' | 'PAUSED' | 'REMOVED';
  headlines?: Array<string | { text: string; pinned_field?: string }>;
  descriptions?: Array<string | { text: string; pinned_field?: string }>;
  final_url?: string;
  // RDA-specific fields
  long_headline?: string;
  business_name?: string;
}

// Image asset data
export interface ImageAssetData {
  asset_id?: string;
  url?: string;
  width?: number;
  height?: number;
}

// Props for sub-components
export interface AssetLevelSelectorProps {
  levels: AssetLevelConfig[];
  selectedLevel: string;
  onChange: (levelId: string) => void;
  disabled?: boolean;
}

export interface AssetTypeSelectorProps {
  assetTypes: TextAssetType[];
  selectedType: TextAssetType;
  onChange: (type: TextAssetType) => void;
  disabled?: boolean;
}

export interface TextAssetsSectionProps {
  campaignId: string;
  campaignType: string;
  entityType: EntityType;
  entityId: string;
  assetType: TextAssetType;
  existingAssets: ExistingAsset[];
  memberUuid: string;
  onAssetsUpdated: () => void;
}

export interface RSAEditorProps {
  campaignId: string;
  adGroupId: string;
  adId: string;
  memberUuid: string;
  onSaved: () => void;
  onCancel: () => void;
}

// Constants
export const CAMPAIGN_LEVEL_ID = 'CAMPAIGN_LEVEL';

export const TEXT_ASSET_LABELS: Record<TextAssetType, string> = {
  headline: 'Headlines',
  description: 'Descriptions',
  long_headline: 'Long Headlines',
  sitelink: 'Sitelinks',
  callout: 'Callouts',
  structured_snippet: 'Structured Snippets',
};

export const TEXT_ASSET_LIMITS: Record<TextAssetType, number> = {
  headline: 30,
  description: 90,
  long_headline: 90,
  sitelink: 25,
  callout: 25,
  structured_snippet: 25,
};

export const IMAGE_TYPE_LABELS: Record<ImageAssetType, string> = {
  marketing_image: 'Landscape (1.91:1)',
  square_marketing_image: 'Square (1:1)',
  portrait_marketing_image: 'Portrait (4:5)',
  logo: 'Logo (1:1)',
  landscape_logo: 'Landscape Logo (4:1)',
};
